import React, { useEffect } from 'react';
import {
  Clock, FileQuestion, FilterX, Inbox, Lock, LogIn, RefreshCw, SearchX, ServerCrash, Sparkles, Wrench, WifiOff,
} from 'lucide-react';
import { ChargeurLocal } from './Chargement';
import { Bandeau } from './Retours';

/* ═══════════════════════════════════════════════════════════════════════
   3. ÉTATS DES DONNÉES

   Un écran sans données n'est jamais un écran blanc. Il dit lequel de ces
   cas il vit, et ce qu'on peut faire :

     vide              rien encore                  → l'action qui en crée
     premiere          rien CRÉÉ par la personne    → l'invitation à commencer
     sansResultat      la recherche ne trouve rien  → effacer la recherche
     sansFiltre        les filtres excluent tout    → réinitialiser les filtres
     erreur            le chargement a échoué       → réessayer
     horsLigne         pas de réseau                → réessayer au retour
     injoignable       réseau là, serveur muet      → réessayer
     session           session absente / expirée    → se reconnecter
     interdit          pas les droits               → (rien à faire ici)
     introuvable       ressource inexistante        → revenir
     limite            trop de requêtes             → patienter
     maintenance       service indisponible         → revenir plus tard

   Deux lignes, jamais une : ce qui manque, puis ce qu'on peut y faire.
   ═══════════════════════════════════════════════════════════════════════ */

const CAS = {
  vide: { Icone: Inbox, titre: 'Rien pour l’instant' },
  premiere: { Icone: Sparkles, titre: 'Commençons' },
  sansResultat: { Icone: SearchX, titre: 'Aucun résultat', aide: 'Essayez d’autres mots, ou effacez la recherche.' },
  sansFiltre: { Icone: FilterX, titre: 'Aucun élément ne correspond aux filtres', aide: 'Élargissez ou réinitialisez les filtres.' },
  erreur: { Icone: ServerCrash, titre: 'Le chargement a échoué', aide: 'Ce n’est pas de votre fait. Réessayez dans un instant.', ton: 'alerte' },
  horsLigne: { Icone: WifiOff, titre: 'Vous êtes hors ligne', aide: 'Vérifiez votre connexion ; la page se rechargera à son retour.', ton: 'attention' },
  injoignable: { Icone: ServerCrash, titre: 'Le serveur ne répond pas', aide: 'Votre connexion fonctionne, mais le serveur est injoignable. Réessayez dans un instant.', ton: 'alerte' },
  session: { Icone: LogIn, titre: 'Votre session a expiré', aide: 'Reconnectez-vous pour continuer.', ton: 'attention' },
  interdit: { Icone: Lock, titre: 'Accès non autorisé', aide: 'Votre compte n’a pas les droits nécessaires. Adressez-vous à un administrateur.', ton: 'attention' },
  introuvable: { Icone: FileQuestion, titre: 'Introuvable', aide: 'Cet élément n’existe pas ou a été supprimé.' },
  limite: { Icone: Clock, titre: 'Trop de demandes en peu de temps', aide: 'Patientez une minute, puis réessayez.', ton: 'attention' },
  maintenance: { Icone: Wrench, titre: 'Service momentanément indisponible', aide: 'Une opération de maintenance est en cours. Revenez dans quelques minutes.', ton: 'attention' },
};

const TEINTES = {
  alerte: 'bg-[var(--alerte-fond)] text-[var(--alerte)]',
  attention: 'bg-[var(--attention-fond)] text-[var(--attention)]',
  neutre: 'bg-[var(--bg-main)] text-[var(--text-muted)]',
};

/** Le cas d'une erreur, d'après son statut HTTP ou l'état du réseau. */
export function casDeLErreur(erreur) {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'horsLigne';
  const statut = erreur?.statut ?? erreur?.status;
  if (statut === 401 || /session expir/i.test(erreur?.message || '')) return 'session';
  if (statut === 403) return 'interdit';
  if (statut === 404) return 'introuvable';
  if (statut === 429) return 'limite';
  if (statut === 503) return 'maintenance';
  if (!statut && /ne répond pas|failed to fetch|network|load failed/i.test(erreur?.message || '')) return 'injoignable';
  return 'erreur';
}

/** Un état sans données, quel qu'il soit. */
export function EtatSansDonnees({ cas = 'vide', titre, aide, action, icone, compact = false, className = '' }) {
  const c = CAS[cas] || CAS.vide;
  const Icone = icone || c.Icone;
  return (
    <div className={`${compact ? 'py-8' : 'py-12'} px-5 text-center ${className}`}
         role={cas === 'erreur' ? 'alert' : 'status'}>
      <span className={`w-[52px] h-[52px] mx-auto mb-3 rounded-full grid place-items-center ${TEINTES[c.ton || 'neutre']}`}>
        <Icone className="w-6 h-6" strokeWidth={1.7} aria-hidden="true" />
      </span>
      <p className="m-0 text-[15px] font-semibold text-[var(--text-bold)]">{titre || c.titre}</p>
      {(aide ?? c.aide) && <p className="mt-1 mb-0 text-[13px] text-[var(--text-muted)] max-w-sm mx-auto leading-relaxed">{aide ?? c.aide}</p>}
      {action && <div className="mt-4 flex justify-center gap-2 flex-wrap">{action}</div>}
    </div>
  );
}

/** L'échec d'un chargement, avec « Réessayer ». */
export function EtatErreur({ erreur, onReessayer, compact, className }) {
  const cas = casDeLErreur(erreur);
  // Hors ligne : on relance de soi-même au retour du réseau, comme annoncé.
  useEffect(() => {
    if (!onReessayer) return undefined;
    const auRetour = () => onReessayer();
    window.addEventListener('online', auRetour);
    return () => window.removeEventListener('online', auRetour);
  }, [onReessayer]);
  // Le message du serveur, quand il en donne un, est plus juste que le nôtre.
  const message = erreur?.message && !/^(Erreur|Error)\b/.test(erreur.message) && cas === 'erreur' ? erreur.message : undefined;
  const action = cas === 'session'
    ? <button type="button" className="act act-sm" onClick={() => window.dispatchEvent(new CustomEvent('assopro:session-expiree'))}><LogIn className="w-4 h-4" />Se reconnecter</button>
    : onReessayer && !['interdit', 'introuvable'].includes(cas)
      ? <button type="button" className="act act-sm act-contour" onClick={onReessayer}><RefreshCw className="w-4 h-4" />Réessayer</button>
      : null;
  return <EtatSansDonnees cas={cas} aide={message} action={action} compact={compact} className={className} />;
}

/**
 * Tout le cycle d'une donnée, en un composant :
 *   <EtatDonnees requete={r} squelette={<SqueletteTableau/>}
 *                vide={{ titre, aide, action }} estVide={(d) => !d.length}
 *                recherche={q} filtresActifs={…} onEffacer={…}>
 *     {(donnees) => <Tableau lignes={donnees} />}
 *   </EtatDonnees>
 */
export function EtatDonnees({
  requete, squelette, children, estVide = (d) => Array.isArray(d) && d.length === 0,
  vide = {}, premiere = false, recherche, filtresActifs, onEffacer, compact,
}) {
  const { statut, donnees, erreur, recharger, erreurActualisation } = requete;
  if (statut === 'chargement') return squelette || <ChargeurLocal />;
  if (statut === 'erreur') return <EtatErreur erreur={erreur} onReessayer={recharger} compact={compact} />;
  if (estVide(donnees)) {
    if (recherche) {
      return <EtatSansDonnees cas="sansResultat" compact={compact}
                              titre={`Aucun résultat pour « ${recherche} »`}
                              action={onEffacer && <button type="button" className="act act-sm act-contour" onClick={onEffacer}>Effacer la recherche</button>} />;
    }
    if (filtresActifs) {
      return <EtatSansDonnees cas="sansFiltre" compact={compact}
                              action={onEffacer && <button type="button" className="act act-sm act-contour" onClick={onEffacer}>Réinitialiser les filtres</button>} />;
    }
    return <EtatSansDonnees cas={premiere ? 'premiere' : 'vide'} compact={compact} {...vide} />;
  }
  return (
    <>
      {erreurActualisation && (
        <div className="mb-3">
          <Bandeau ton="attention" action={<button type="button" className="act act-sm act-contour" onClick={recharger}>Réessayer</button>}>
            L’actualisation a échoué : ces données sont peut-être anciennes.
          </Bandeau>
        </div>
      )}
      {children(donnees)}
    </>
  );
}

/** Certaines parties ont échoué, d'autres non : on montre ce qui est là. */
export function AvisDonneesPartielles({ manquantes = [], onReessayer }) {
  if (!manquantes.length) return null;
  return (
    <Bandeau ton="attention" titre="Une partie de la page n’a pas pu être chargée"
             action={onReessayer && <button type="button" className="act act-sm act-contour" onClick={onReessayer}><RefreshCw className="w-4 h-4" />Réessayer</button>}>
      Indisponible pour l’instant : {manquantes.join(', ')}.
    </Bandeau>
  );
}

